import { motion } from 'framer-motion';
import { isFunction } from 'lodash';
import { useRecoilState, useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil';

import { usePorts } from '@/hooks/use-ports';
import { useRouterQuery } from '@/hooks/use-router-query';
import { useUserSettings } from '@/hooks/use-user-settings';
import { DISTANCE_UNITS, PORT_CONNECTION_TYPES } from '@/lib/constants';
import { portInsightsMapInstanceState } from '@/state/map';
import {
  connectionsDirectionState,
  filteredPortsState,
  globalPortsReset,
  hoveredPortState,
  isSearchingAllPortsState,
  locationAutocompleteValueState,
  portsFiltersState,
  portsSearchParamsState,
  portsState,
  portsWithinViewPortState,
  searchRadiusState,
  selectedPortDetailsState,
  selectedPortsState,
  showConnectionsState,
} from '@/state/ports';
import { isSearchingLocationAutocompleteState } from '@/state/search';
import { PortInterface } from '@/types';
import { tv } from '@/utils/styles';
import { trackUser } from '@/utils/tracking';

import { centerMapToPort } from '../map/utils';
import { PortDetails } from './port-details';
import { PortsInfo } from './ports-info';
import { PortsList } from './ports-list';

const motionVariants = {
  visible: {
    opacity: 1,
    transition: {
      delay: 0.6,
      duration: 0.6,
    },
  },
  hidden: {
    opacity: 0,
    transition: {
      delay: 0.6,
      duration: 0.6,
    },
  },
};

const Results = () => {
  const { performPortLookup } = usePorts();
  const { distanceUnits } = useUserSettings();
  const { getRouteSummary, handleNearbyListingClose } = usePorts();
  const { addUrlQueryParam, removeUrlQueryParam } = useRouterQuery();

  const searchParams = useRecoilValue(portsSearchParamsState);
  const mapInstance = useRecoilValue(portInsightsMapInstanceState);
  const ports = useRecoilValue(portsState);
  const [selectedPorts, setSelectedPorts] = useRecoilState(selectedPortsState);
  const [hoveredPort, setHoveredPort] = useRecoilState(hoveredPortState);
  const selectedPortDetails = useRecoilValue(selectedPortDetailsState);
  const filteredPorts = useRecoilValue(filteredPortsState);
  const portsFilters = useRecoilValue(portsFiltersState);
  const portsWithinViewPort = useRecoilValue(portsWithinViewPortState);
  const setLocationAutocompleteValue = useSetRecoilState(locationAutocompleteValueState);
  const searchRadius = useRecoilValue(searchRadiusState);
  const isSearchingAllPorts = useRecoilValue(isSearchingAllPortsState);
  const [isSearchingLocationAutocomplete, setIsSearchingLocationAutocomplete] = useRecoilState(
    isSearchingLocationAutocompleteState,
  );
  const locationAutocompleteValue = useRecoilValue(locationAutocompleteValueState);
  const setConnectionsDirection = useSetRecoilState(connectionsDirectionState);
  const [showConnections, setShowConnections] = useRecoilState(showConnectionsState);
  const resetPorts = useResetRecoilState(globalPortsReset);

  const { origin } = searchParams;

  const handleSelectedPortClose = () => {
    // If there is a place in the autocomplete, we're going back to the
    // ports list and so we need to add the place to the URL again
    if (locationAutocompleteValue) {
      addUrlQueryParam('place', locationAutocompleteValue);
    }

    removeUrlQueryParam('port');
    // Analytics Event
    trackUser.event('Close Selected Port');

    resetPorts();
  };

  const handleItemClick = (port: PortInterface) => {
    if (isFunction(performPortLookup)) {
      performPortLookup(port);
    }

    // Analytics Event
    trackUser.event('Select Port in List');
  };

  const handleConnectionsSelect = () => {
    if (!selectedPorts) return;

    setShowConnections(!showConnections);

    // Hide any existing port route connections
    setSelectedPorts((prev) => {
      return {
        ...prev,
        origin: prev?.origin ? prev.origin : null,
        destination: null,
      };
    });

    if (mapInstance) {
      centerMapToPort(selectedPorts?.origin, mapInstance);
    }
  };

  const handleItemHover = (port: PortInterface | null) => {
    setHoveredPort(port);
  };

  const handleNearbyPortsSearch = (formattedTypesenseName: string) => {
    setIsSearchingLocationAutocomplete('origin');
    setLocationAutocompleteValue(formattedTypesenseName);

    // Analytics Event
    trackUser.event('Nearby Ports Search', { place: formattedTypesenseName });
  };

  const handleConnectionsDirectionChange = (value: boolean) => {
    const booleanToValue = () => (value ? PORT_CONNECTION_TYPES.OUTBOUND : PORT_CONNECTION_TYPES.INBOUND);

    setConnectionsDirection(booleanToValue());

    const originPort = value ? selectedPorts?.origin : selectedPorts?.destination;
    const destinationPort = value ? selectedPorts?.destination : selectedPorts?.origin;

    if (originPort && destinationPort && isFunction(getRouteSummary)) {
      getRouteSummary({
        pol: originPort,
        pod: destinationPort,
      });
    }
    // Analytics Event
    trackUser.event('Change Port Connection Direction', { value: booleanToValue() });
  };

  const { base, inner, info } = styles();

  const renderContents = () => {
    if (selectedPorts?.origin) {
      return (
        <motion.div className={base()} animate={{ height: '100%' }} layoutId="results">
          <motion.div
            className={inner()}
            initial="hidden"
            animate="visible"
            exit="hidden"
            variants={motionVariants}
            key="motion-details"
          >
            <PortDetails
              handleConnectionsSelect={handleConnectionsSelect}
              handleNearbyPortsSearch={handleNearbyPortsSearch}
              handleSelectedPortClose={handleSelectedPortClose}
              handleConnectionsDirectionChange={handleConnectionsDirectionChange}
              isSearchingNearbyPorts={!!isSearchingLocationAutocomplete}
              searchOrigin={origin}
              selectedPort={selectedPorts?.origin}
              selectedPortDetails={selectedPortDetails}
              distanceUnits={distanceUnits?.value || DISTANCE_UNITS.MI}
            />
          </motion.div>
        </motion.div>
      );
    }

    // Starting state when you get to the page
    if (!origin) {
      return (
        <motion.div className={base()} animate={{ height: '100%' }} layoutId="results">
          <motion.div
            className={info()}
            initial="hidden"
            animate="visible"
            exit="hidden"
            variants={motionVariants}
            key="motion-info"
          >
            <PortsInfo
              ports={ports}
              portsWithinViewPort={portsWithinViewPort}
              isSearchingAllPorts={isSearchingAllPorts}
            />
          </motion.div>
        </motion.div>
      );
    }

    return (
      <motion.div className={base({ className: 'bg-grey-50' })} animate={{ height: '100%' }} layoutId="results">
        <motion.div
          className={inner()}
          initial="hidden"
          animate="visible"
          exit="hidden"
          variants={motionVariants}
          key="motion-list"
        >
          <PortsList
            currentTransportType={portsFilters.transportType}
            filteredPorts={filteredPorts}
            handleItemClick={handleItemClick}
            handleItemHover={handleItemHover}
            handleNearbyListingClose={handleNearbyListingClose}
            hoveredPort={hoveredPort}
            searchOrigin={origin}
            searchRadius={searchRadius}
            distanceUnits={distanceUnits?.value || DISTANCE_UNITS.MI}
          />
        </motion.div>
      </motion.div>
    );
  };

  return renderContents();
};

const styles = tv({
  slots: {
    base: 'relative flex max-h-[calc(100dvh-var(--port-insights-search-bar-height)-theme(spacing.32))] rounded-md border border-grey-300 bg-grey-100 bg-white p-4 shadow-md md:max-h-[calc(100dvh-var(--port-insights-search-bar-height)-theme(spacing.20))] 2xl:p-5',
    inner: 'flex w-full flex-col',
    info: 'flex w-full content-between items-center',
  },
});

export { Results };
